import { getBackendOptions, MultiBackend, NodeModel, Tree } from "@minoru/react-dnd-treeview";
import { LightIcon, PlusIcon } from "@xola/icons";
import { Alert, Button } from "@xola/ui-kit";
import { useDebounceEffect } from "ahooks";
import { sortBy } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { DndProvider } from "react-dnd";
import { CollectionNode, CollectionNodeObject, MAX_COLLECTION_NESTING_DEPTH } from "types";
import { HeaderToolBarActions } from "../../../Common/HeaderToolbarActions";
import { ContentLayout } from "../../../Layout/ContentLayout";
import { Loader } from "../../../Loader";
import { CollectionItem as CollectionItemRow } from "./CollectionItem";
import { buildTreeNodes, canDropNode, TreeNode } from "./CollectionListHelper";
import { CustomDragPreview, DropPlaceholder } from "./CustomDragPreview";
import { DropErrorModal } from "./DropErrorModal";
import { EditCollectionModal } from "./EditCollectionModal";
import { api } from "@/api/api";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { getCollectionDropSequence, getCollectionNestingDepth, getConstructedItemPath } from "@/models/CollectionNode";

export const CollectionList = () => {
    const { data: allCollections, isLoading } = useAutoFetchCollection<CollectionNode>("collectionNodes", {
        object: CollectionNodeObject.GROUP_COLLECTION_NODE,
        childrenCount: true,
    });
    const addCollectionModal = useModal();
    const dropErrorModal = useModal();
    const invalidate = useInvalidate();
    const [isDragging, setIsDragging] = useState(false);
    const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // Local state for optimistic updates - store tree data directly
    const [treeData, setTreeData] = useState<TreeNode[]>([]);

    // Memoize tree building to avoid recalculation on every render
    const builtTreeData = useMemo(() => buildTreeNodes(allCollections), [allCollections]);

    // Sync tree data with server data when it changes
    useDebounceEffect(
        () => {
            setTreeData(builtTreeData);
        },
        [builtTreeData],
        { leading: true, trailing: true, maxWait: 200 },
    );

    // Auto-scroll when dragging near edges
    useEffect(() => {
        if (!isDragging) {
            if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current);
                scrollIntervalRef.current = null;
            }
            return;
        }

        const mouseMoveHandler = (e: MouseEvent) => handleMouseMove(scrollIntervalRef, e);

        window.addEventListener("mousemove", mouseMoveHandler);

        return () => {
            window.removeEventListener("mousemove", mouseMoveHandler);
            if (scrollIntervalRef.current) {
                clearInterval(scrollIntervalRef.current);
            }
        };
    }, [isDragging]);

    const handleDrop = async (
        newTree: NodeModel<CollectionNode>[],
        options: { dragSourceId?: string | number; dropTargetId?: string | number; relativeIndex?: number },
    ) => {
        setIsDragging(false);

        // Clear any active scrolling immediately on drop
        if (scrollIntervalRef.current) {
            clearInterval(scrollIntervalRef.current);
            scrollIntervalRef.current = null;
        }

        const { dragSourceId, dropTargetId, relativeIndex } = options;

        if (!dragSourceId || relativeIndex === undefined) return;

        // Find the dragged node and new parent from newTree
        const draggedNode = newTree.find((node) => node.id === dragSourceId);
        const newParentNode = newTree.find((node) => node.id === dropTargetId);

        if (!draggedNode?.data) return;

        // Get siblings collection items for sequence calculation
        const isDroppedOnRootLevel = dropTargetId === 0 || dropTargetId === undefined;
        const siblings: CollectionNode[] = treeData
            .filter((node) => (isDroppedOnRootLevel ? node.parent === 0 : node.parent === dropTargetId))
            .map((node) => node.data)
            .filter((data): data is CollectionNode => data !== undefined);

        // Get collectionChildrenDepth from treeData
        const draggedTreeNode = treeData.find((node) => node.id === dragSourceId);

        // Calculate the new path, sequence, and validate the drop operation
        const dropResult = calculateDropResult(
            draggedNode,
            newParentNode,
            dropTargetId,
            relativeIndex,
            siblings,
            draggedTreeNode?.collectionChildrenDepth,
        );

        if (!dropResult.isValid) {
            if (dropResult.exceedsDepthLimit) {
                dropErrorModal.show();
            }
            return;
        }

        // Check if path and sequence are unchanged
        if (draggedNode.data.path === dropResult.newPath && draggedNode.data.sequence === dropResult.newSequence) {
            return;
        }
        // Optimistically update tree data with the new positions
        setTreeData(newTree);

        try {
            await api.patch(`/collectionNodes/${dragSourceId}`, {
                path: dropResult.newPath,
                sequence: dropResult.newSequence,
            });

            invalidate("collectionNodes");
        } catch {
            // Revert optimistic update on failure
            setTreeData(builtTreeData);
        }
    };

    const canDrop = (
        tree: NodeModel<CollectionNode>[],
        options: {
            dragSourceId?: string | number;
            dropTargetId?: string | number;
            dragSource?: NodeModel<CollectionNode>;
            dropTarget?: NodeModel<CollectionNode>;
        },
    ) => {
        const { dragSourceId, dropTargetId, dragSource, dropTarget } = options;
        return canDropNode(dragSourceId, dropTargetId, dragSource?.data, dropTarget?.data);
    };

    const renderNode = (node: NodeModel<CollectionNode>, { depth, isOpen, onToggle, handleRef }: any) => (
        <CollectionItemRow
            node={node}
            depth={depth}
            isOpen={isOpen}
            handleRef={handleRef}
            isDragging={isDragging}
            onToggle={onToggle}
        />
    );

    if (isLoading) {
        return <Loader />;
    }

    return (
        <ContentLayout isWide>
            <HeaderToolBarActions>
                <Button color="primary" icon={<PlusIcon />} onClick={addCollectionModal.show}>
                    Create Collection
                </Button>
            </HeaderToolBarActions>
            <div className="mt-5 space-y-4 px-1.5 pb-40">
                <div className="text-xl font-bold">Collections</div>
                {allCollections.length > 0 ? (
                    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
                        <Tree
                            tree={treeData}
                            rootId={0}
                            insertDroppableFirst={false}
                            sort={false}
                            dropTargetOffset={10}
                            classes={{
                                draggingSource: "opacity-30",
                                root: "space-y-4",
                                listItem: (node) =>
                                    node.parent === 0 ? "rounded pt-1 pb-2 border border-gray-light" : "",
                            }}
                            render={renderNode}
                            dragPreviewRender={(monitorProps) => <CustomDragPreview monitorProps={monitorProps} />}
                            placeholderRender={(node, { depth }) => <DropPlaceholder depth={depth} />}
                            canDrop={canDrop}
                            canDrag={(node) => node?.data?.object === CollectionNodeObject.GROUP_COLLECTION_NODE}
                            onDragStart={() => setIsDragging(true)}
                            onDragEnd={() => setIsDragging(false)}
                            onDrop={handleDrop}
                        />
                    </DndProvider>
                ) : (
                    <NoCollectionsAlert onAddCollectionClick={addCollectionModal.show} />
                )}
            </div>
            <EditCollectionModal isOpen={addCollectionModal.isVisible} onClose={addCollectionModal.hide} />
            <DropErrorModal isOpen={dropErrorModal.isVisible} onClose={dropErrorModal.hide} />
        </ContentLayout>
    );
};

const NoCollectionsAlert = ({ onAddCollectionClick }: { onAddCollectionClick: () => void }) => {
    return (
        <Alert color="primary" className="flex flex-col gap-4">
            <div className="flex p-2">
                <LightIcon size="large" />
                <div className="ml-2">
                    <div className="mb-3 flex space-x-2">
                        <span className="text-md font-bold" data-testid="collection-list-no-collections-found">
                            No Collections Found
                        </span>
                    </div>
                    <span className="text-base">
                        You do not have any collections yet. Please create your first collection to get started.
                    </span>
                </div>
            </div>
            <div className="flex justify-end">
                <Button size="small" icon={<PlusIcon />} onClick={onAddCollectionClick}>
                    Add First Collection
                </Button>
            </div>
        </Alert>
    );
};

interface DropCalculationResult {
    newPath: string;
    newSequence: number;
    isValid: boolean;
    exceedsDepthLimit?: boolean;
}

const calculateDropResult = (
    draggedNode: NodeModel<CollectionNode> | undefined,
    newParentNode: NodeModel<CollectionNode> | undefined,
    dropTargetId: string | number | undefined,
    relativeIndex: number,
    siblings: CollectionNode[],
    draggedNodeDepth?: number,
): DropCalculationResult => {
    if (!draggedNode?.data) {
        return { newPath: "", newSequence: 0, isValid: false };
    }
    // Calculate new depth
    let newParentPath = "";
    let newParentDepth = 0;

    if (dropTargetId !== 0 && dropTargetId !== undefined && newParentNode?.data?.path) {
        newParentPath = newParentNode.data.path;
        newParentDepth = getCollectionNestingDepth(newParentPath);
    }

    // Check depth limits when not dropping at root level
    if (dropTargetId !== 0) {
        const newDepth = newParentDepth + 1;
        const nestedDepth = draggedNodeDepth ?? 0;
        const resultingMaxDepth = newDepth + nestedDepth;

        if (resultingMaxDepth > MAX_COLLECTION_NESTING_DEPTH) {
            return { newPath: "", newSequence: 0, isValid: false, exceedsDepthLimit: true };
        }
    }

    // Calculate new path
    const newPath =
        dropTargetId === 0 || dropTargetId === undefined
            ? ""
            : getConstructedItemPath(newParentPath, String(dropTargetId));

    // Calculate new sequence
    const sortedSiblings = sortBy(siblings, "sequence");
    const newSequence = getCollectionDropSequence(relativeIndex, sortedSiblings);

    return { newPath, newSequence, isValid: true };
};

const handleMouseMove = (scrollIntervalRef: React.MutableRefObject<NodeJS.Timeout | null>, e: MouseEvent) => {
    const scrollThreshold = 150; // pixels from edge to trigger scroll
    const scrollSpeed = 15; // pixels to scroll per interval

    const viewportHeight = window.innerHeight;
    const mouseY = e.clientY;

    // Clear existing interval
    if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
    }

    // Check if we can still scroll up or down
    const canScrollUp = window.scrollY > 0;
    const canScrollDown = window.scrollY + window.innerHeight < document.documentElement.scrollHeight;

    // Scroll up when near top and can scroll
    if (mouseY < scrollThreshold && canScrollUp) {
        scrollIntervalRef.current = setInterval(() => {
            window.scrollBy({ top: -scrollSpeed, behavior: "auto" });
        }, 16); // ~60fps
    }
    // Scroll down when near bottom and can scroll
    else if (mouseY > viewportHeight - scrollThreshold && canScrollDown) {
        scrollIntervalRef.current = setInterval(() => {
            window.scrollBy({ top: scrollSpeed, behavior: "auto" });
        }, 16);
    }
};
